import { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { DeleteOutlineOutlined } from '@mui/icons-material';

import { FOutlinedValue, FTimePicker } from '../../../../../../../components/baseV2';
import { RATE_TIME_FORMAT } from '../constants';
import {
  RATE_EVENT_DELETE_BUTTON_SX,
  RATE_EVENT_EDIT_FIELD_SX,
  RATE_EVENT_EDIT_ROW_SX,
  RATE_EVENT_FIELD_SX,
  RATE_EVENT_FIELD_TEXT_SX,
} from '../styles';
import { formatRateEventTime, resolveRateEventTime } from '../utils';

import type { Moment } from 'moment';
import type { RateEventFieldProps } from '../types';

function RateEventField({
  label,
  time,
  minTime,
  maxTime,
  disabled = false,
  editable = false,
  onDelete,
  onUpdate,
}: RateEventFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftTime, setDraftTime] = useState(time);

  useEffect(() => {
    setDraftTime(time);
  }, [time]);

  const handleFieldClick = () => {
    if (!disabled && editable) {
      setIsEditing(true);
    }
  };

  const handleTimeChange = (value: Moment | null) => {
    setDraftTime(resolveRateEventTime(value, minTime, maxTime));
  };

  const handleTimeAccept = (value: Moment | null) => {
    const resolvedTime = resolveRateEventTime(value, minTime, maxTime);
    if (!resolvedTime) return;

    setDraftTime(resolvedTime);
    onUpdate?.(resolvedTime);
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Box onClick={handleFieldClick}>
        <FOutlinedValue label={label} disabled={disabled} sx={RATE_EVENT_FIELD_SX}>
          <Typography sx={RATE_EVENT_FIELD_TEXT_SX}>
            {formatRateEventTime(time)}
          </Typography>
        </FOutlinedValue>
      </Box>
    );
  }

  return (
    <Box sx={RATE_EVENT_EDIT_ROW_SX}>
      <FTimePicker
        label={label}
        fullWidth
        format={RATE_TIME_FORMAT}
        open={isEditing}
        showClockIcon
        use12Hours
        disabled={disabled}
        value={draftTime}
        minTime={minTime ?? undefined}
        maxTime={maxTime ?? undefined}
        sx={RATE_EVENT_EDIT_FIELD_SX}
        onOpen={() => setIsEditing(true)}
        onChange={handleTimeChange}
        onAccept={handleTimeAccept}
        onClose={() => setIsEditing(false)}
      />
      {onDelete != null ? (
        <IconButton
          aria-label={`Delete ${label}`}
          sx={RATE_EVENT_DELETE_BUTTON_SX}
          disabled={disabled}
          onClick={onDelete}
        >
          <DeleteOutlineOutlined />
        </IconButton>
      ) : null}
    </Box>
  );
}

export default RateEventField;
